import React, { useEffect, useCallback, useRef, useMemo, useState } from 'react'
import { Button, Modal, ModalBody } from 'reactstrap'
import { PUBSUB_GROUP_EVENT } from '@common'

/**
 * PaymentEventModal Component
 *
 * Handles different payment events with appropriate UI/UX:
 * - PAYMENT_LATER: Shows order number and asks customer to return later
 * - PAYMENT_REQUEST: Redirects to payment QR code page
 * - PAYMENT_SUCCESS: Shows order number and thank you message
 *
 * Props:
 * - isVisible: boolean - controls modal visibility
 * - eventType: string - type of payment event
 * - eventData: object - event payload data
 * - onClose: function - callback when modal is closed
 * - onPaymentRequest: function - callback for payment request (redirect)
 */
const PaymentEventModal = ({
    isVisible = false,
    eventType = null,
    eventData = {},
    onClose = () => {},
    onPaymentRequest = () => {},
}) => {
    const autoCloseTimerRef = useRef(null)
    const hasHandledPaymentRequest = useRef(false)
    const lastEventDataRef = useRef(null)
    const onPaymentRequestRef = useRef(onPaymentRequest)
    const onCloseRef = useRef(onClose)
    const eventDataRef = useRef(eventData)

    // Track if DOM is ready before rendering Modal
    const [isDOMReady, setIsDOMReady] = useState(false)

    // Keep refs updated with latest functions
    useEffect(() => {
        onPaymentRequestRef.current = onPaymentRequest
    }, [onPaymentRequest])

    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    // Keep eventData ref updated
    useEffect(() => {
        eventDataRef.current = eventData
    }, [eventData])

    // Memoize eventData key to avoid unnecessary re-renders
    const eventDataKey = useMemo(() => {
        return JSON.stringify(eventData)
    }, [eventData])

    // Check if DOM is ready before rendering Modal
    // This prevents errors when Modal tries to access body element during navigation
    useEffect(() => {
        // If modal should not be visible, reset DOM ready state
        if (!isVisible || !eventType) {
            setIsDOMReady(false)
            return
        }

        // Check if body element exists
        const checkDOMReady = () => {
            if (typeof window !== 'undefined' && document.body) {
                setIsDOMReady(true)
                return true
            }
            return false
        }

        // Check immediately
        if (checkDOMReady()) {
            return
        }

        // If body doesn't exist, wait for it with timeout
        let attempts = 0
        const maxAttempts = 100 // 5 seconds max (100 * 50ms)
        const checkInterval = setInterval(() => {
            attempts++
            if (checkDOMReady()) {
                clearInterval(checkInterval)
            } else if (attempts >= maxAttempts) {
                clearInterval(checkInterval)
                // Even if body is not found, set ready to prevent infinite waiting
                // Modal will handle the error gracefully
                setIsDOMReady(true)
            }
        }, 50)

        return () => {
            clearInterval(checkInterval)
        }
    }, [isVisible, eventType])

    // Auto-close timer for success and later events
    useEffect(() => {
        const validEventTypes = [
            PUBSUB_GROUP_EVENT.PAYMENT_SUCCESS,
            PUBSUB_GROUP_EVENT.PAYMENT_LATER,
            PUBSUB_GROUP_EVENT.PAYMENT_CANCEL,
            PUBSUB_GROUP_EVENT.PAYMENT_FAILED,
            PUBSUB_GROUP_EVENT.PAYMENT_PAYWAVE_SUCCESS,
        ]

        // Clear any existing timer
        if (autoCloseTimerRef.current) {
            clearTimeout(autoCloseTimerRef.current)
            autoCloseTimerRef.current = null
        }

        if (isVisible && validEventTypes.includes(eventType)) {
            const timer = setTimeout(() => {
                onCloseRef.current()
            }, 10000)

            autoCloseTimerRef.current = timer

            return () => {
                if (autoCloseTimerRef.current) {
                    clearTimeout(autoCloseTimerRef.current)
                    autoCloseTimerRef.current = null
                }
            }
        }
    }, [isVisible, eventType])

    // Auto-redirect timer for payment requests
    useEffect(() => {
        if (isVisible && eventType === PUBSUB_GROUP_EVENT.PAYMENT_REQUEST) {
            // Prevent duplicate execution by checking if we've already handled this event
            const currentEventData = eventDataRef.current
            if (!hasHandledPaymentRequest.current || lastEventDataRef.current !== eventDataKey) {
                hasHandledPaymentRequest.current = true
                lastEventDataRef.current = eventDataKey
                // Use setTimeout to defer execution and prevent state update during render
                setTimeout(() => {
                    onPaymentRequestRef.current(currentEventData)
                }, 0)
            }
        } else if (!isVisible) {
            // Only reset flag when modal is actually closed (not just when eventType changes)
            // This prevents re-triggering when reopening with same eventData
            hasHandledPaymentRequest.current = false
            lastEventDataRef.current = null
        }
    }, [isVisible, eventType, eventDataKey])

    const handleClose = useCallback(() => {
        if (autoCloseTimerRef.current) {
            clearTimeout(autoCloseTimerRef.current)
            autoCloseTimerRef.current = null
        }

        // Close modal first, cleanup will happen in onClosed callback
        onCloseRef.current()
    }, [])

    const renderContent = () => {
        switch (eventType) {
            case PUBSUB_GROUP_EVENT.PAYMENT_LATER:
                return (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <div className="display-1 mb-3">
                                <i className="bx bx-time-five text-warning" style={{ fontSize: '5rem' }}></i>
                            </div>
                            <h2 className="display-6 fw-bold mb-3">
                                {eventData.meta?.isFromQuickCheckout ? 'Please Pay at Drink Stall Cashier' : 'Payment Later'}
                            </h2>
                            <p className="fs-4 text-muted mb-4">Please remember your order number</p>
                        </div>

                        <div className="bg-light rounded-4 p-4 mb-4">
                            <div className="text-muted mb-2" style={{ fontSize: '1.2rem' }}>
                                Order Number
                            </div>
                            <div className="display-4 fw-bold text-primary">
                                #{eventData.number || eventData.orderId || 'N/A'}
                            </div>
                        </div>

                        {/* <p className="fs-5 text-muted mb-4">Please return to complete your payment when ready</p> */}

                        <Button
                            color="primary"
                            size="lg"
                            onClick={handleClose}
                            style={{ fontSize: '1.2rem', padding: '12px 32px' }}
                        >
                            OK, Got It
                        </Button>
                    </div>
                )

            case PUBSUB_GROUP_EVENT.PAYMENT_REQUEST:
                return (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <div className="display-1 mb-3">
                                {' '}
                                <i className="bx bx-credit-card text-primary"></i>
                            </div>
                            <h2 className="display-6 fw-bold mb-3">Payment Required</h2>
                            <p className="fs-4 text-muted mb-4">Redirecting to payment page in a moment...</p>
                        </div>

                        <div className="bg-light rounded-4 p-4 mb-4">
                            <div className="text-muted mb-2" style={{ fontSize: '1.2rem' }}>
                                Amount
                            </div>
                            <div className="display-5 fw-bold text-primary">
                                {(() => {
                                    const amount = eventData.summary?.finalAmount || eventData.amount || 0
                                    return amount === 0 ? 'Free' : `$${amount.toFixed(2)}`
                                })()}
                            </div>
                        </div>

                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                )

            case PUBSUB_GROUP_EVENT.PAYMENT_SUCCESS:
                return (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <div className="display-1 mb-3">
                                <i className="bx bx-check-circle text-primary"></i>
                            </div>
                            <h2 className="display-6 fw-bold mb-3 text-primary">Payment Successful!</h2>
                            <p className="fs-4 text-muted mb-4">Thank you for your order</p>
                        </div>

                        <div className="bg-primary bg-opacity-10 rounded-4 p-4 mb-4 border-opacity-25">
                            <div className="text-muted mb-2" style={{ fontSize: '1.2rem' }}>
                                Order Number
                            </div>
                            <div className="display-4 fw-bold text-primary">
                                #{eventData.number || eventData.orderId || 'N/A'}
                            </div>
                        </div>

                        {/* <p className="fs-5 text-muted mb-4">
                            Your order is being prepared. Please wait for your number to be called.
                        </p> */}

                        <Button
                            color="primary"
                            size="lg"
                            onClick={handleClose}
                            style={{ fontSize: '1.2rem', padding: '12px 32px' }}
                        >
                            Thank You!
                        </Button>
                    </div>
                )

            case PUBSUB_GROUP_EVENT.PAYMENT_FAILED:
                return (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <div className="display-1 mb-3">
                                <i className="bx bx-x-circle text-danger"></i>
                            </div>
                            <h2 className="display-6 fw-bold mb-3 text-danger">Payment Failed!</h2>
                            <p className="fs-4 text-muted mb-4">Please try again or inform the cashier</p>
                        </div>

                        <Button
                            color="danger"
                            size="lg"
                            onClick={handleClose}
                            style={{ fontSize: '1.2rem', padding: '12px 32px' }}
                        >
                            Close
                        </Button>
                    </div>
                )

            case PUBSUB_GROUP_EVENT.PAYMENT_CANCEL:
                return (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <div className="display-1 mb-3">
                                <i className="bx bx-x-circle text-danger"></i>
                            </div>
                            <h2 className="display-6 fw-bold mb-3 text-danger">Payment Cancelled!</h2>
                            <p className="fs-4 text-muted mb-4">Please try again or inform the cashier</p>
                        </div>

                        <Button
                            color="danger"
                            size="lg"
                            onClick={handleClose}
                            style={{ fontSize: '1.2rem', padding: '12px 32px' }}
                        >
                            Close
                        </Button>
                    </div>
                )

            case PUBSUB_GROUP_EVENT.PAYMENT_PAYWAVE_SUCCESS:
                return (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <div className="display-1 mb-3">
                                <i className="bx bx-check-circle text-primary"></i>
                            </div>
                            <h2 className="display-6 fw-bold mb-3 text-primary">PayWave Payment Successful!</h2>
                            <p className="fs-4 text-muted mb-4">Thank you for your order</p>
                        </div>

                        <div className="bg-primary bg-opacity-10 rounded-4 p-4 mb-4 border-opacity-25">
                            <div className="text-muted mb-2" style={{ fontSize: '1.2rem' }}>
                                Order Number
                            </div>
                            <div className="display-4 fw-bold text-primary">
                                #{eventData.number || eventData.orderId || 'N/A'}
                            </div>
                        </div>

                        <Button
                            color="primary"
                            size="lg"
                            onClick={handleClose}
                            style={{ fontSize: '1.2rem', padding: '12px 32px' }}
                        >
                            Thank You!
                        </Button>
                    </div>
                )

            default:
                return (
                    <div className="text-center py-5">
                        <p className="fs-4 text-muted">Unknown payment event</p>
                        <Button color="secondary" onClick={handleClose}>
                            Close
                        </Button>
                    </div>
                )
        }
    }

    // Don't render if DOM is not ready or modal should not be visible
    if (!isVisible || !eventType || !isDOMReady) {
        return null
    }

    return (
        <Modal
            isOpen={isVisible}
            toggle={handleClose}
            centered
            size="lg"
            backdrop="static"
            keyboard={false}
            style={{ zIndex: 10000 }}
        >
            <ModalBody className="p-0">{renderContent()}</ModalBody>
        </Modal>
    )
}

export default PaymentEventModal
